<script setup lang="ts">
import type { LicenseEntitlementNumericKey, LicenseLimitStatus, LicenseScope } from '#layers/license/types/license';
import type { DialogEmits } from '#layers/ui/public';
import { useEmitAsProps } from 'reka-ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { LICENSE_CONTACT_SALES_URL } from '#layers/license/constants/urls';

const { entitlementKey, status, allowance } = defineProps<{
	entitlementKey: LicenseEntitlementNumericKey;
	status: Exclude<LicenseLimitStatus, 'allow'>;
	allowance: number;
	canManageLicense: boolean;
	scope?: LicenseScope;
}>();

const emit = defineEmits<DialogEmits>();
const forwarded = useEmitAsProps(emit);

const { t } = useI18n();

const countLabel = computed(() => t(`organization.dashboard.settings.license.entitlement_count.${entitlementKey}`, allowance));

const notIncluded = computed(() => allowance === 0);

const titleKey = computed(() => {
	if (notIncluded.value) return 'license.limit.not_included.title';
	return status === 'warn' ? 'license.limit.warn.title' : 'license.limit.blocked.title';
});
const bodyKey = computed(() => status === 'warn' ? 'license.limit.warn.body' : 'license.limit.blocked.body');

const confirm = computed(() => status === 'warn' ? { label: t('license.limit.create_anyway') } : false);
const cancel = computed(() => ({ label: status === 'warn' ? t('common.cancel') : t('common.close') }));
</script>

<template>
	<DDialog v-bind="forwarded" :confirm :cancel :close="false">
		<template #title>
			<span class="flex items-center gap-2">
				{{ $t(titleKey) }}
				<LicenseEntitlementLimitBadge v-if="!notIncluded" :entitlement-key :scope />
			</span>
		</template>

		<template #body>
			<div class="flex flex-col gap-y-4">
				<i18n-t :keypath="bodyKey" tag="p" scope="global" class="text-muted">
					<template #count>
						<span class="font-semibold">
							{{ countLabel }}
						</span>
					</template>
				</i18n-t>

				<i18n-t
					v-if="canManageLicense"
					keypath="license.limit.contact_sales_notice"
					tag="p"
					scope="global"
					class="text-muted"
				>
					<template #link>
						<DTextLink :to="LICENSE_CONTACT_SALES_URL" target="_blank">
							{{ $t('license.limit.contact_sales') }}
						</DTextLink>
					</template>
				</i18n-t>

				<p v-else class="text-muted">
					{{ $t('license.limit.contact_admin_notice') }}
				</p>
			</div>
		</template>
	</DDialog>
</template>
